import { useMemo } from "react";
import type { Course } from "@/core/types";
import { Icon, type IconName } from "@/ui/components/Icon";
import { CurriculumBand, StatusRing, moduleSteps } from "@/ui/components/Progress";
import {
  courseStats,
  findNextLesson,
  formatMinutes,
  lookupFor,
  modulesInProgress,
  recentlyCompleted,
  sumStats,
} from "@/ui/progressStats";
import { NextLessonCard, formatDate, formatSaved, greeting } from "./parts";
import type { AllProgress } from "./useAllProgress";
import { href } from "./useHashRoute";

interface WebHomeProps {
  courses: Course[];
  progress: AllProgress;
}

/** Beide Listen unten zeigen gleich viele Zeilen, damit die Reihe gerade endet. */
const LIST_ROWS = 4;

/** Wie Lernen und Prüfen zusammenspielen – für alle, die noch nichts erledigt haben. */
const HOW_TO: Array<{ icon: IconName; title: string; text: string }> = [
  {
    icon: "book",
    title: "Kurs im Seitenpanel öffnen",
    text: "In Word, Excel oder PowerPoint: Start → Kurs → Kurs öffnen",
  },
  {
    icon: "task",
    title: "Aufgabe im Dokument erledigen",
    text: "Jeder Schritt erklärt erst und sagt dann, was zu tun ist",
  },
  {
    icon: "check",
    title: "Aufgabe prüfen lassen",
    text: "Das Add-in liest dein Dokument und zeigt, was noch fehlt",
  },
  {
    icon: "search",
    title: "Hier nachlesen",
    text: "Jede Lektion lässt sich auch im Browser lesen – Strg + K sucht",
  },
];

export function WebHome({ courses, progress }: WebHomeProps) {
  const now = useMemo(() => new Date(), []);

  const rows = courses.map((course) => {
    const saved = progress.get(course.id);
    const lookup = lookupFor(saved);
    return {
      course,
      saved,
      lookup,
      stats: courseStats(course, lookup),
      next: findNextLesson(course, lookup, saved.lastLessonKey),
    };
  });
  const total = sumStats(rows.map((row) => row.stats));
  const longest = Math.max(...rows.map((row) => row.stats.stepsTotal));

  // Der Kurs, an dem zuletzt gearbeitet wurde – er bestimmt Begrüßung und „Als Nächstes“.
  const active =
    rows
      .filter((row) => row.stats.stepsDone > 0)
      .sort((a, b) => (b.saved.updatedAt ?? "").localeCompare(a.saved.updatedAt ?? ""))[0] ??
    null;
  const focus = active ?? rows[0];

  const inProgress = rows.flatMap((row) =>
    modulesInProgress(row.course, row.lookup).map((entry) => ({ ...entry, course: row.course })),
  );
  // Offene Module: erst die angefangenen, dann die nächsten noch nicht
  // begonnenen des Kurses, an dem zuletzt gearbeitet wurde.
  const upcoming = focus.course.modules
    .map((module) => ({ module, course: focus.course, ...moduleSteps(module, focus.lookup) }))
    .filter(({ done }) => done === 0);
  const recent = active ? recentlyCompleted(active.course, active.saved, LIST_ROWS) : [];
  // Beide Listen zeigen gleich viele Zeilen. Ohne abgeschlossene Lektion
  // steht rechts die Anleitung mit ebenfalls vier Zeilen.
  const listRows = recent.length === 0 ? LIST_ROWS : recent.length;
  const openModules = [...inProgress, ...upcoming].slice(0, listRows);
  // Der eine Befund des Tages, direkt unter der Begrüßung.
  let headline: string;
  let finding: string;
  if (!active) {
    headline = `${greeting(now)}. Noch ist nichts erledigt.`;
    finding = `${courses.length} Kurse mit ${total.lessonsTotal} Lektionen und ${total.stepsTotal} Schritten, zusammen ${formatMinutes(total.minutesTotal)}.`;
  } else if (total.stepsDone === total.stepsTotal) {
    headline = `${greeting(now)}. Alle Kurse sind abgeschlossen.`;
    finding = `${total.lessonsTotal} Lektionen in ${formatMinutes(total.minutesTotal)}.`;
  } else {
    headline = `${greeting(now)}. ${active.course.host} steht bei ${active.stats.percentage} %.`;
    const next = active.next;
    if (next) {
      const left = next.module.lessons.reduce(
        (sum, lesson) =>
          sum +
          lesson.steps.filter((step) => !active.lookup.isStepDone(next.module.id, lesson.id, step.id))
            .length,
        0,
      );
      finding = `Noch ${left} ${left === 1 ? "Schritt" : "Schritte"} bis „${next.module.title}“ fertig ist.`;
    } else {
      finding = `Der ${active.course.title} ist abgeschlossen.`;
    }
  }

  const savedAt = formatSaved(active?.saved.updatedAt, now);

  return (
    <div className="w-page w-home">
      <header className="w-greeting">
        <h1>{headline}</h1>
        <p>
          {formatDate(now)} · {finding}
        </p>
      </header>

      <div className="w-grid">
        <dl className="w-tiles">
          <div className="w-tile">
            <dt>Schritte erledigt</dt>
            <dd className="w-tile__value num">
              {total.stepsDone} <small>/ {total.stepsTotal}</small>
            </dd>
            <dd className="w-tile__caption num">{total.percentage} % aller Kurse</dd>
          </div>
          <div className="w-tile">
            <dt>Lektionen abgeschlossen</dt>
            <dd className="w-tile__value num">
              {total.lessonsDone} <small>/ {total.lessonsTotal}</small>
            </dd>
            <dd className="w-tile__caption num">
              {total.lessonsTotal - total.lessonsDone} noch offen
            </dd>
          </div>
          <div className="w-tile">
            <dt>Module abgeschlossen</dt>
            <dd className="w-tile__value num">
              {total.modulesDone} <small>/ {total.modulesTotal}</small>
            </dd>
            <dd className="w-tile__caption num">{inProgress.length} angefangen</dd>
          </div>
          <div className="w-tile">
            <dt>Lernzeit geschafft</dt>
            <dd className="w-tile__value num">{formatMinutes(total.minutesDone)}</dd>
            <dd className="w-tile__caption num">von {formatMinutes(total.minutesTotal)}</dd>
          </div>
        </dl>

        <section className="w-panel w-courses" aria-labelledby="kurse-titel">
          <header className="w-panel__head">
            <h2 className="w-panel__title" id="kurse-titel">
              Deine Kurse
            </h2>
            <span className="w-panel__meta">Breite der Bänder = Anzahl Schritte</span>
          </header>
          <ul className="w-card w-course-list">
            {rows.map(({ course, lookup, stats, next }) => (
              <li key={course.id} className="w-course" data-host={course.host}>
                <div className="w-course__head">
                  <a className="w-course__name" href={href.course(course.id)}>
                    {course.title}
                  </a>
                  <span className="w-course__pct num">
                    {stats.percentage}
                    <small> %</small>
                  </span>
                </div>
                <div
                  className="w-course__band"
                  style={{ width: `${(stats.stepsTotal / longest) * 100}%` }}
                >
                  <CurriculumBand
                    course={course}
                    progress={lookup}
                    onSelectModule={() => {
                      window.location.hash = href.course(course.id);
                    }}
                  />
                </div>
                <p className="w-course__foot num">
                  <span>
                    {stats.stepsDone} von {stats.stepsTotal} Schritten · {stats.modulesTotal} Module
                  </span>
                  {next ? (
                    <a
                      className="w-link"
                      href={href.lesson(course.id, next.module.id, next.lesson.id)}
                    >
                      {stats.stepsDone === 0 ? "Beginnen mit" : "Weiter mit"} „{next.lesson.title}“
                    </a>
                  ) : (
                    <span>Abgeschlossen</span>
                  )}
                </p>
              </li>
            ))}
          </ul>
        </section>

        <div className="w-side">
          <NextLessonCard
            course={focus.course}
            next={focus.next}
            lookup={focus.lookup}
            showCourse
            showGoals
          />
          <section className="w-panel w-saved" aria-labelledby="gespeichert-titel">
            <h2 className="w-panel__title" id="gespeichert-titel">
              Zuletzt gespeichert
            </h2>
            <p className="w-saved__value">{savedAt ?? <span className="w-dash">–</span>}</p>
            <p className="w-panel__meta">in diesem Browser</p>
          </section>
        </div>

        <section className="w-panel w-list-panel" aria-labelledby="arbeit-titel">
          <header className="w-panel__head">
            <h2 className="w-panel__title" id="arbeit-titel">
              Offene Module
            </h2>
            <span className="w-panel__meta num">{inProgress.length} angefangen</span>
          </header>
          {openModules.length === 0 ? (
            <p className="w-empty">Alle Module sind abgeschlossen.</p>
          ) : (
            <ul className="w-rows">
              {openModules.map(({ module, course, done, total }) => (
                <li key={`${course.id}/${module.id}`} data-host={course.host}>
                  <a className="w-row" href={href.course(course.id)}>
                    <StatusRing fraction={done / total} />
                    <span className="w-row__text">
                      <span className="w-row__title">{module.title}</span>
                      <span className="w-row__meta">
                        {course.host} · {done === 0 ? "noch nicht angefangen" : "angefangen"}
                      </span>
                    </span>
                    <span className="w-row__figure num">
                      {done}
                      <small>/{total} Schritte</small>
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </section>

        {active && recent.length > 0 ? (
          <section className="w-panel w-list-panel" aria-labelledby="fertig-titel">
            <header className="w-panel__head">
              <h2 className="w-panel__title" id="fertig-titel">
                Zuletzt abgeschlossen in {active.course.host}
              </h2>
              <a className="w-link" href={href.course(active.course.id)}>
                Alle {active.stats.lessonsDone} anzeigen
                <Icon name="chevronRight" size={14} />
              </a>
            </header>
            <ul className="w-rows">
              {recent.map(({ module, lesson }) => (
                <li key={`${module.id}/${lesson.id}`} data-host={active.course.host}>
                  <a
                    className="w-row"
                    href={href.lesson(active.course.id, module.id, lesson.id)}
                  >
                    <StatusRing fraction={1} />
                    <span className="w-row__text">
                      <span className="w-row__title">{lesson.title}</span>
                      <span className="w-row__meta">{module.title}</span>
                    </span>
                    <span className="w-row__figure num">{lesson.durationMinutes} Min.</span>
                  </a>
                </li>
              ))}
            </ul>
          </section>
        ) : (
          <section className="w-panel w-list-panel" aria-labelledby="anleitung-titel">
            <header className="w-panel__head">
              <h2 className="w-panel__title" id="anleitung-titel">
                So kommst du voran
              </h2>
            </header>
            <ol className="w-rows">
              {HOW_TO.map((item) => (
                <li key={item.title} className="w-row w-row--static">
                  <Icon name={item.icon} className="w-row__icon" />
                  <span className="w-row__text">
                    <span className="w-row__title">{item.title}</span>
                    <span className="w-row__meta">{item.text}</span>
                  </span>
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>
    </div>
  );
}
